/**
 * Bridging server validation into a form library.
 *
 * The API answers a 422 with `details` keyed by request path -
 * `body.email`, `body.seats.0.password` - and a form library keys errors by
 * field path within its own values. Translating between the two by hand in
 * every form is how a field error ends up with nowhere to render, which fails
 * silently: the submit does nothing and the page looks unchanged.
 *
 * Deliberately free of any dependency on Formik or on a portal's ApiError, so
 * this file stays copyable into every portal and testable on its own.
 */

/** The `details` shape the error envelope carries for a 422. */
export type ServerFieldErrors = Record<string, string[]>;

/**
 * Reads a dotted path out of an object.
 *
 * Written here rather than imported so this module keeps no dependencies.
 * Numeric segments index arrays, so `seats.0.password` resolves the way the
 * server names it.
 */
function readPath(source: unknown, path: string): unknown {
  let current: unknown = source;

  for (const segment of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

/**
 * Turns server field errors into form field errors.
 *
 * The `body.` prefix is stripped because a form's values are the body; the rest
 * of the path is left exactly as the server wrote it, which is already the
 * dotted form Formik's `setFieldError` understands.
 *
 * Only the first message per field is kept. A field showing three complaints at
 * once is noise, and the first is the one the person has to fix anyway.
 */
export function toFormErrors(details: ServerFieldErrors | undefined): Record<string, string> {
  if (!details) return {};

  const out: Record<string, string> = {};

  for (const [key, messages] of Object.entries(details)) {
    const message = messages[0];
    if (message === undefined) continue;

    // query. and params. prefixes exist server-side but never describe a form.
    if (key.startsWith('query.') || key.startsWith('params.')) continue;

    out[key.replace(/^body\./, '')] = message;
  }

  return out;
}

/**
 * Splits mapped errors into the ones a form can show and the ones it cannot.
 *
 * `unplaced` is the important half. A form that suppresses its error banner
 * whenever field errors arrive - the normal thing to do, since they belong
 * beside their inputs - will show nothing at all for a field it has no input
 * for. That is a silent failed submit, and it is worth detecting rather than
 * hoping every schema change is mirrored in the UI.
 *
 * Placeability is decided against the form's own values rather than a
 * hand-written list of paths, so it cannot fall out of date.
 */
export function splitFormErrors(
  errors: Record<string, string>,
  values: unknown,
): { placed: Record<string, string>; unplaced: string[] } {
  const placed: Record<string, string> = {};
  const unplaced: string[] = [];

  for (const [path, message] of Object.entries(errors)) {
    if (readPath(values, path) === undefined) unplaced.push(path);
    else placed[path] = message;
  }

  return { placed, unplaced };
}
